import { TRANSCODE_TO_JPEG_IMAGE_TYPES } from 'src/services/MediaCompression/MediaCompression.constants';
import {
  isTranscodeOnlyImage,
  isTranscodeOnlyMedia,
  tTranscodeCandidate,
} from 'src/services/MediaCompression/MediaCompression.functions';
import { tTemplateMediaKind } from 'src/utils/templateMedia';

export const allowedTypesInstagram = [
  // Imágenes
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  // HEIC/HEIF (iPhone photos) are accepted here so the file picker's `accept`
  // and the drop-zone validation let them through; sendFileMessage transcodes
  // them to JPEG before upload (BUG-712). Spread from the transcode list rather
  // than re-typed, so adding a transcode-only format in one place cannot leave
  // the picker silently rejecting it. Empty-type .heic (no MIME reported at all)
  // is additionally allowed via isAcceptableForChannel below.
  ...TRANSCODE_TO_JPEG_IMAGE_TYPES,
  // Videos
  'video/mp4',
  'video/webm',
  'video/ogg',
  //'video/quicktime', This is temporary.
  // Audios
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/mp3',
  'audio/aac',
];

const documentTypes = [
  // Documents
  'text/csv',
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/xml',
  'text/xml',
  'application/zip',
  // Raw MIME types reported by Chrome on Windows for .zip — kept here so the file picker
  // accept attribute recognises them. FileUpload.service.ts normalises these to
  // 'application/zip' before calling the presign endpoint. Both this list and
  // MIME_NORMALIZATION in FileUpload.service.ts must stay in sync.
  'application/x-zip-compressed',
  'application/x-zip',
];

export const allowedTypesTiktok = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  // Transcoded to JPEG before send (BUG-712).
  ...TRANSCODE_TO_JPEG_IMAGE_TYPES,
];

export const allowedTypesAll = [...allowedTypesInstagram, ...documentTypes];

export function getAllowedTypesForChannel(channel: string | undefined): string[] {
  if (channel === 'TIKTOK') return allowedTypesTiktok;
  if (channel === 'INSTAGRAM') return allowedTypesInstagram;
  return allowedTypesAll;
}

/**
 * The single gate every attachment surface must ask. A raw MIME allow-list check
 * is not enough: HEIC/HEIF frequently arrive with an empty `File.type`, and they
 * are legal because sendFileMessage transcodes them to JPEG before upload.
 *
 * Accepts anything with `{ type, name }`, so the drop zones (which hold `File`s)
 * and the attachment tray (which holds `tChatAttachment`s) reach the same
 * verdict. They previously did not, and a tray entry the drop zone had accepted
 * could later be re-judged invalid and disable the Send button.
 */
export function isAcceptableForChannel(
  candidate: tTranscodeCandidate,
  channel: string | undefined,
): boolean {
  return (
    getAllowedTypesForChannel(channel).includes(candidate.type) ||
    isTranscodeOnlyMedia(candidate)
  );
}

const MEGABYTE = 1024 * 1024;

const META_LIMITS = {
  image: 8 * MEGABYTE,
  video: 25 * MEGABYTE,
  audio: 25 * MEGABYTE,
  document: 25 * MEGABYTE,
};

const WAPI_LIMITS = {
  image: 5 * MEGABYTE,
  video: 16 * MEGABYTE,
  audio: 16 * MEGABYTE,
  document: 100 * MEGABYTE,
};

export type tWapiTemplateMediaLimits = {
  limit: string;
  imageLimit: string;
  videoLimit: string;
  documentLimit: string;
};

/**
 * Size limits the template header guidance announces, read from WAPI_LIMITS
 * so the copy cannot drift from the cap the uploader enforces. The guidance
 * kept saying a flat "máx. 5 MB" after BUG-714 raised video to 16 MB and
 * documents to 100 MB (BUG-1039). `limit` is the one for the header's kind;
 * the generic `file` kind has none of its own and its guidance lists the
 * three per-kind limits instead.
 */
export function getWapiTemplateMediaLimits(
  kind: tTemplateMediaKind,
): tWapiTemplateMediaLimits {
  const toLabel = (bytes: number) => `${bytes / MEGABYTE} MB`;
  return {
    limit: kind === 'file' ? '' : toLabel(WAPI_LIMITS[kind]),
    imageLimit: toLabel(WAPI_LIMITS.image),
    videoLimit: toLabel(WAPI_LIMITS.video),
    documentLimit: toLabel(WAPI_LIMITS.document),
  };
}

const DEFAULT_LIMITS = {
  pdf: 40 * MEGABYTE,
  other: 16 * MEGABYTE,
};

function detectMediaKind(file: File): 'image' | 'video' | 'audio' | 'document' {
  // An empty-type `.heic` reports no MIME, but sendFileMessage classifies it as
  // an IMAGE and transcodes it. This preflight must agree, or the file is sized
  // against the far looser document limit (WAPI: 100MB vs 5MB), sails past the
  // drop gate, and only fails after the user has already composed their message.
  if (file.type.startsWith('image/') || isTranscodeOnlyImage(file))
    return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
}

/**
 * Returns the practical max attachment size for a given channel + file.
 *
 * Limits source: Meta documentation
 * (https://developers.facebook.com/docs/messenger-platform/send-messages,
 *  https://developers.facebook.com/docs/whatsapp/cloud-api/reference/media).
 *
 * - MESSENGER / INSTAGRAM use Meta Messenger Platform API limits.
 * - WHATSAPP_API uses WhatsApp Business Cloud API limits.
 * - WHATSAPP_WEB / TIKTOK / undefined keep the previous 16MB / 40MB-PDF behavior.
 */
export function getMaxFileSizeForChannel(
  channel: string | undefined,
  file: File,
): { maxSize: number; humanLimit: string } {
  if (channel === 'MESSENGER' || channel === 'INSTAGRAM') {
    const maxSize = META_LIMITS[detectMediaKind(file)];
    return { maxSize, humanLimit: `${maxSize / MEGABYTE}MB` };
  }

  if (channel === 'WHATSAPP_API') {
    const maxSize = WAPI_LIMITS[detectMediaKind(file)];
    return { maxSize, humanLimit: `${maxSize / MEGABYTE}MB` };
  }

  const isPdf =
    file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
  const maxSize = isPdf ? DEFAULT_LIMITS.pdf : DEFAULT_LIMITS.other;
  return { maxSize, humanLimit: isPdf ? '40MB' : '16MB' };
}

/**
 * True when the browser handed over a file with no bytes, as it sometimes does
 * for a pasted image or a file still being written or synced. Uploading it
 * stores an empty object and the send fails: Meta answers 131053 "No media
 * found from weblink" (BUG-676). Every attach path refuses it up front.
 */
export function isEmptyFile(file: File): boolean {
  return file.size === 0;
}
